import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { loginRequest } from "config/msal-config";

const BRAND_BLUE = "#007CBF";
const BRAND_NAVY = "#012848";

function MicrosoftIcon() {
  return (
    <Box component="span" sx={{ display: "inline-flex", mr: 1.25, lineHeight: 0 }}>
      <svg width="20" height="20" viewBox="0 0 21 21" aria-hidden="true">
        <rect x="1" y="1" width="9" height="9" fill="#F25022" />
        <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
        <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
        <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
      </svg>
    </Box>
  );
}

function AdminLogin() {
  const { instance } = useMsal();
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  const handleLogin = async () => {
    setIsLoggingIn(true);
    setLoginError(null);

    try {
      await instance
        .loginPopup(loginRequest)
        .then(() => instance.acquireTokenSilent(loginRequest))
        .catch((error) => {
          if (error instanceof InteractionRequiredAuthError) {
            return instance.acquireTokenPopup(loginRequest);
          }
          throw error;
        });
    } catch (error) {
      console.error("Azure AD login failed:", error);
      setLoginError("Sign-in could not be completed. Please try again.");
    } finally {
      setIsLoggingIn(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        bgcolor: "#f5f7fa",
      }}
    >
      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          flex: 1,
          flexDirection: "column",
          justifyContent: "center",
          px: 6,
          py: 8,
          background: `linear-gradient(145deg, ${BRAND_NAVY} 0%, ${BRAND_BLUE} 100%)`,
          color: "#fff",
        }}
      >
        <Stack spacing={4} sx={{ maxWidth: 420 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <VerifiedUserOutlinedIcon sx={{ fontSize: 36, opacity: 0.95 }} />
            <Typography variant="h5" fontWeight={700} letterSpacing={0.2}>
              KYC Admin Portal
            </Typography>
          </Stack>

          <Stack spacing={1.5}>
            <Typography variant="h4" fontWeight={700} lineHeight={1.25}>
              Customer verification, simplified.
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.88, lineHeight: 1.6 }}>
              Review KYC status, verification outcomes, and customer records in one
              secure workspace for internal teams.
            </Typography>
          </Stack>

          <Stack spacing={1.25} sx={{ opacity: 0.9 }}>
            <Typography variant="body2">• Verified and pending customer views</Typography>
            <Typography variant="body2">• PAN and bank verification insights</Typography>
            <Typography variant="body2">• Secure Bayer single sign-on</Typography>
          </Stack>
        </Stack>
      </Box>

      <Box
        sx={{
          flex: { xs: 1, md: "0 0 480px", lg: "0 0 520px" },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 3,
          py: { xs: 6, md: 4 },
        }}
      >
        <Stack
          spacing={3}
          sx={{
            width: "100%",
            maxWidth: 400,
            p: { xs: 3, sm: 4 },
            bgcolor: "#fff",
            borderRadius: 2,
            boxShadow: "0 8px 32px rgba(1, 40, 72, 0.1)",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Stack spacing={1.5} alignItems={{ xs: "center", md: "flex-start" }}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ display: { md: "none" } }}
            >
              <VerifiedUserOutlinedIcon sx={{ color: BRAND_NAVY, fontSize: 28 }} />
              <Typography variant="h6" fontWeight={700} color={BRAND_NAVY}>
                KYC Admin Portal
              </Typography>
            </Stack>

            <Typography
              variant="h5"
              fontWeight={700}
              color={BRAND_NAVY}
              textAlign={{ xs: "center", md: "left" }}
            >
              Sign in
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign={{ xs: "center", md: "left" }}
            >
              Use your Bayer Microsoft account to continue. Access is restricted to
              authorized users.
            </Typography>
          </Stack>

          {loginError && (
            <Alert severity="error" onClose={() => setLoginError(null)}>
              {loginError}
            </Alert>
          )}

          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={handleLogin}
            disabled={isLoggingIn}
            sx={{
              height: 48,
              bgcolor: BRAND_BLUE,
              "&:hover": { bgcolor: "#006aa3" },
            }}
          >
            {isLoggingIn ? (
              <CircularProgress size={22} color="inherit" />
            ) : (
              <>
                <MicrosoftIcon />
                Sign in with Microsoft
              </>
            )}
          </Button>

          <Divider />

          <Typography variant="caption" color="text.secondary" textAlign="center">
            By signing in, you agree to use this portal only for approved business
            purposes. Need access? Contact your portal administrator.
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
}

export default AdminLogin;
